import type { Job } from "../types/job";

// Display helpers for job fields the backend stores raw: row names are a mix
// of English, Turkish and bare op ids ("add-logo"), model is a provider id
// ("fal-ai", "gemini-3-pro-image-preview"), status can be one the catalog
// does not know ("cancelled").

type Translate = (key: string, opts?: Record<string, any>) => string;
interface I18nLike {
  exists: (key: string) => boolean;
}

const normalize = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");

// "video-to-video" → "Video to video" — last resort for a type with no name.
const humanize = (s: string) => {
  const words = s.replace(/[-_]+/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : "";
};

// Image-editing rows: metadata.operation is the stable id; `name` is an English
// label, a Turkish one ("Toplu Alan İşlemleri") or the bare op id.
export function jobDisplayName(job: Job, t: Translate, i18n: I18nLike): string {
  const candidates = [job.metadata?.operation, job.name, job.type];
  for (const c of candidates) {
    if (typeof c !== "string" || !c.trim()) continue;
    const key = `jobNames.${normalize(c)}`;
    if (i18n.exists(key)) return t(key);
  }
  return job.name || humanize(job.type || "") || t("modal.job");
}

// Internal processing libraries, not something the user picked.
const HIDDEN_MODELS = new Set(["sharp", "sharp-chroma-key", "external"]);
const EXACT_MODELS: Record<string, string> = {
  "fal-ai": "fal.ai",
  "florence-2-large": "Florence-2",
  "gemini-3-pro-image-preview": "Gemini 3 Pro Image",
  "gpt-5.6": "GPT-5.6",
  imagen4: "Imagen 4",
};
const WORDS: Record<string, string> = {
  ai: "AI",
  gpt: "GPT",
  sdxl: "SDXL",
  ltx: "LTX",
  tts: "TTS",
  elevenlabs: "ElevenLabs",
  pixverse: "PixVerse",
  heygen: "HeyGen",
  liveportrait: "LivePortrait",
  omnihuman: "OmniHuman",
  openai: "OpenAI",
};

// "seedance-2.0-mini" → "Seedance 2.0 Mini", "auto" → localized, internal libs → null.
// Every "veo*" id → "Omni": they all run Gemini Omni since 2026-10-23.
export function formatModel(model: string | null | undefined, t: Translate): string | null {
  const raw = String(model || "").trim();
  const lower = raw.toLowerCase();
  if (!raw || HIDDEN_MODELS.has(lower)) return null;
  if (lower === "auto") return t("jobCard.modelAuto");
  if (EXACT_MODELS[lower]) return EXACT_MODELS[lower];
  if (/^veo(-|$)/.test(lower)) return "Omni";
  return raw
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => WORDS[w.toLowerCase()] ?? (/^v?\d/i.test(w) ? w : w[0].toUpperCase() + w.slice(1)))
    .join(" ");
}

export function statusText(status: string | null | undefined, t: Translate, i18n: I18nLike) {
  const key = `status.${(status || "unknown").toLowerCase()}`;
  return i18n.exists(key) ? t(key) : t("status.unknown");
}

export const hasScene = (job: Pick<Job, "sceneIndex">) =>
  job.sceneIndex !== null && job.sceneIndex !== undefined && String(job.sceneIndex) !== "";
